"use client";

import { cn } from "@/lib/utils";
import { useEffect, useRef, useState } from "react";

export type FinancialsChartBar = {
  key: string;
  label: string;
  /** Raw numeric value used for proportions. */
  value: number;
  /** Pre-formatted currency string (server formatted). */
  formatted: string;
  tone?: "black" | "green" | "blue";
};

export type FinancialsChartMultiple = {
  key: string;
  /** e.g. "2.4×" */
  value: string;
  /** e.g. "price to SDE" */
  label: string;
};

export type ListingFinancialsChartProps = {
  bars: FinancialsChartBar[];
  multiples?: FinancialsChartMultiple[];
  className?: string;
};

const toneClass: Record<NonNullable<FinancialsChartBar["tone"]>, string> = {
  black: "bg-primary",
  green: "bg-[#00C853]",
  blue: "bg-[#2962FF]",
};

/**
 * Three (or fewer) horizontal proportion bars — asking price vs revenue vs
 * SDE — plus the derived multiples. Bars grow once when scrolled into view.
 */
export function ListingFinancialsChart({
  bars,
  multiples = [],
  className,
}: ListingFinancialsChartProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [grown, setGrown] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const prefersReduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    if (prefersReduced || typeof IntersectionObserver === "undefined") {
      setGrown(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setGrown(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const max = Math.max(...bars.map((b) => b.value), 1);

  return (
    <div ref={ref} className={cn("flex flex-col gap-6", className)}>
      <ul className="flex flex-col gap-4" aria-label="Asking price compared to revenue and earnings">
        {bars.map((bar, index) => {
          const pct = Math.max((bar.value / max) * 100, 1.5);
          return (
            <li key={bar.key} className="flex flex-col gap-1.5">
              <div className="flex items-baseline justify-between gap-4 text-sm">
                <span className="text-muted-foreground">{bar.label}</span>
                <span className="font-data font-semibold tabular-nums text-foreground">
                  {bar.formatted}
                </span>
              </div>
              <div
                className="h-2 w-full overflow-hidden rounded-full bg-muted"
                role="img"
                aria-label={`${bar.label}: ${bar.formatted}`}
              >
                <div
                  className={cn(
                    "h-full rounded-full transition-[width] duration-700 ease-out",
                    toneClass[bar.tone ?? "black"],
                  )}
                  style={{
                    width: grown ? `${pct}%` : "0%",
                    transitionDelay: `${index * 90}ms`,
                  }}
                />
              </div>
            </li>
          );
        })}
      </ul>

      {multiples.length > 0 && (
        <dl className="flex flex-wrap gap-x-8 gap-y-3">
          {multiples.map((m) => (
            <div key={m.key} className="flex flex-col">
              <dd className="font-data text-xl font-semibold tracking-tight tabular-nums text-foreground">
                {m.value}
              </dd>
              <dt className="text-xs text-muted-foreground">{m.label}</dt>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}

ListingFinancialsChart.displayName = "ListingFinancialsChart";
